import { useState, useEffect } from 'react';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { HelpCircle, Heart, Sun, Moon, ExternalLink } from 'lucide-react';

const FAQ_ITEMS = [
    {
        q: "What is Songwriter's Toolbox?",
        a: "A local-first notebook for unfinished songs, fragments, and musical ideas — lyrics, voice memos, tunings, tempos, and notes — all in one place."
    },
    {
        q: "Where is my work stored?",
        a: "Everything stays on your device. Nothing is uploaded, tracked, or shared."
    },
    {
        q: "Do I need an account?",
        a: "No. Just open it and start."
    },
    {
        q: "What happens if I stop using this app?",
        a: "Nothing. Your songs remain on your device, and you can export them anytime."
    },
    {
        q: "Is this meant to help me finish songs?",
        a: "Not directly. It's meant to help you remember what you were doing when inspiration struck, so you can return to it later if you want."
    },
    {
        q: "Can I take my songs with me?",
        a: "Yes. You can export lyrics, recordings, or full song packages."
    },
    {
        q: "Who built this?",
        a: "Songwriter's Toolbox was built by Sean Hines, a singer/songwriter originally from Chattanooga, TN, currently residing in Orange County, CA."
    }
];

export default function AboutDrawer() {
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState('dark');

    // Load theme preference on mount
    useEffect(() => {
        const stored = localStorage.getItem('stb-theme');
        if (stored) {
            setTheme(stored);
            document.documentElement.classList.toggle('light', stored === 'light');
        } else {
            // Respect system preference on first load
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            const initialTheme = prefersDark ? 'dark' : 'light';
            setTheme(initialTheme);
            document.documentElement.classList.toggle('light', initialTheme === 'light');
        }
    }, []);

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('stb-theme', newTheme);
        document.documentElement.classList.toggle('light', newTheme === 'light');
    };

    return (
        <Drawer open={open} onOpenChange={setOpen}>
            <DrawerTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-foreground"
                    title="About & Settings"
                >
                    <HelpCircle className="w-5 h-5" />
                </Button>
            </DrawerTrigger>
            <DrawerContent className="max-h-[85vh]">
                <div className="mx-auto w-full max-w-lg">
                    <DrawerHeader className="text-left pb-2">
                        <DrawerTitle className="font-display text-xl">
                            About Songwriter's Toolbox
                        </DrawerTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            A local-first notebook for unfinished songs and musical ideas.
                        </p>
                    </DrawerHeader>
                    
                    <div className="px-4 pb-6 overflow-y-auto max-h-[60vh]">
                        {/* Theme Toggle */}
                        <div className="flex items-center justify-between py-4 border-b border-border">
                            <div>
                                <span className="text-sm font-medium">Theme</span>
                                <p className="text-xs text-muted-foreground">
                                    {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={toggleTheme}
                                className="h-9 gap-2"
                            >
                                {theme === 'dark' ? (
                                    <>
                                        <Sun className="w-4 h-4" />
                                        Light
                                    </>
                                ) : (
                                    <>
                                        <Moon className="w-4 h-4" />
                                        Dark
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* FAQ Section */}
                        <div className="py-4 space-y-4">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                Questions
                            </h3>
                            {FAQ_ITEMS.map((item, i) => (
                                <div key={i} className="space-y-1">
                                    <h4 className="text-sm font-medium text-foreground">
                                        {item.q}
                                    </h4>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        {item.a}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Support Section */}
                        <div className="pt-4 border-t border-border">
                            <p className="text-sm text-muted-foreground mb-3">
                                If this tool has been helpful, you can support its continued development.
                            </p>
                            <a
                                href="https://buymeacoffee.com/songwriterstools"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                            >
                                <Heart className="w-4 h-4" />
                                Support Songwriter's Toolbox
                                <ExternalLink className="w-3 h-3 opacity-50" />
                            </a>
                        </div>

                        {/* Quiet footer */}
                        <p className="text-xs text-muted-foreground/60 text-center mt-8 italic">
                            You don't need to finish anything here.
                        </p>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
